import React from 'react';
import PropTypes from 'prop-types';

import {Dialog, DialogActions, DialogContent, DialogTitle} from 'views/components';
import {Alert, Button, CircularProgress, Stack, TextField, Typography} from 'views/components';
import {ClientAutocomplete, Translate, translate} from 'views/module';

const GUIDANCE_LIMIT = 2000;

const GenerateJobDialog = (props) => {
  const {regenerate, selectClient, onClose, onGenerate} = props;

  const [guidance, setGuidance] = React.useState('');
  const [client, setClient] = React.useState(null);
  const [working, setWorking] = React.useState(false);

  const tooLong = guidance.trim().length > GUIDANCE_LIMIT;
  const missingClient = selectClient && !client;
  // The draft keeps running if the dialog closes, so it stays open until Loom answers.
  const close = working ? undefined : onClose;

  const submit = async () => {
    setWorking(true);
    await onGenerate(guidance, client);
    setWorking(false);
  };

  return (
    <Dialog onClose={close}>
      <DialogTitle onClose={close}>
        <Translate
          expression={regenerate ?
            'job.ClientJobs.GenerateDialog.RegenerateTitle' :
            'job.ClientJobs.GenerateDialog.GenerateTitle'}
        />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{pt: 1}}>
          {
            regenerate &&
            <Alert severity='warning'>
              <Translate expression='job.ClientJobs.GenerateDialog.Overwrite' />
            </Alert>
          }
          {
            selectClient &&
            <ClientAutocomplete
              loom
              label={translate('job.ClientJobs.GenerateDialog.Client')}
              statuses={[]}
              selectedClient={client}
              setSelectedClient={setClient}
              onClear={() => setClient(null)}
            />
          }
          <TextField
            fullWidth
            multiline
            minRows={3}
            disabled={working}
            label={translate('job.ClientJobs.GenerateDialog.Guidance')}
            value={guidance}
            onChange={setGuidance}
            helperText={`${guidance.trim().length}/${GUIDANCE_LIMIT}`}
            slotProps={{formHelperText: {sx: {color: tooLong ? 'error.main' : 'text.secondary'}}}}
          />
          {
            working &&
            <Stack direction='row' spacing={1} sx={{alignItems: 'center'}}>
              <CircularProgress size={20} />
              <Typography sx={{fontSize: '14px', color: 'text.secondary'}}>
                <Translate expression='job.ClientJobs.GenerateDialog.Working' />
              </Typography>
            </Stack>
          }
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' disabled={working} onClick={onClose}>
          <Translate expression='generic.Cancel' />
        </Button>
        <Button disabled={working || tooLong || missingClient} onClick={submit}>
          <Translate
            expression={regenerate ?
              'job.ClientJobs.Regenerate' :
              'job.ClientJobs.GenerateDialog.Generate'}
          />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

GenerateJobDialog.propTypes = {
  regenerate: PropTypes.bool,
  selectClient: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onGenerate: PropTypes.func.isRequired,
};

export default React.memo(GenerateJobDialog);
